import { useEffect, useState } from 'react';
import { api } from '../helpers/api/api';
import { isUserAdmin, isUserAuthenticated } from '../services/locationService';
import { getLastClientCode, saveLastClientCode, removeLastClientCode } from './localStorage/clientStorageUtils';
import { validateClientCode } from './useClientValidation';

type RedirectPathResult = {
    redirectPath: string;
    isLoading: boolean;
};

export const useRedirectPath = (): RedirectPathResult => {
    const [redirectPath, setRedirectPath] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const determineRedirectPath = async () => {
            try {
                // Start with default path
                let path = 'clients';

                if (!isUserAuthenticated()) {
                    path = await determineUnauthenticatedPath();
                } else {
                    path = determineAuthenticatedPath();
                }

                setRedirectPath(path);
            } catch (error) {
                console.error('Error determining redirect path:', error);
                // Reset last client when falling back to clients due to error
                removeLastClientCode();
                setRedirectPath('clients');
            } finally {
                setIsLoading(false);
            }
        };

        determineRedirectPath();
    }, []);

    return { redirectPath, isLoading };
};

const determineUnauthenticatedPath = async (): Promise<string> => {
    const lastClientCode = getLastClientCode();
    if (!lastClientCode) return 'clients';

    const clientExists = await validateClientCode(lastClientCode);

    if (clientExists) {
        return `${lastClientCode}/locations/map`;
    } else {
        // Reset last client when redirecting to clients path
        removeLastClientCode();
        return 'clients';
    }
};

const determineAuthenticatedPath = (): string => {
    if (isUserAdmin()) {
        // For admin users, go to clients and reset the last client
        removeLastClientCode();
        return 'clients';
    }

    const clientCode = api.getLoggedInUser()?.clientCode;
    if (clientCode) {
        saveLastClientCode(clientCode);
        return `${clientCode}/locations/map`;
    }

    // If client not found, reset last client
    removeLastClientCode();
    return 'clientNotFound';
};
